/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useEffect, useRef, useState } from "react";

const INACTIVITY_THRESHOLD_MS = 60 * 1000;

export type EpochMs = number;

type UseActivityTimerOptions = Readonly<{
  startPaused?: boolean;
  inactivityThresholdMs?: number;
  storageKey?: string;
}>;

const readStoredMilliseconds = (key: string | undefined): number => {
  if (!key) return 0;
  const stored = localStorage.getItem(key);
  if (!stored) return 0;
  const parsed = parseInt(stored, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export const useActivityTimer = (options?: UseActivityTimerOptions) => {
  const {
    startPaused = false,
    inactivityThresholdMs = INACTIVITY_THRESHOLD_MS,
    storageKey,
  } = options ?? {};

  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;

  const initialMs = useRef(readStoredMilliseconds(storageKey)).current;
  const [elapsedSeconds, setElapsedSeconds] = useState(
    Math.floor(initialMs / 1000)
  );
  const [isActive, setIsActive] = useState(!startPaused);
  const [manuallyPaused, setManuallyPaused] = useState(startPaused);
  const [mountTimestamp] = useState(() => Date.now());
  const [sessionStartedAt, setSessionStartedAt] = useState<EpochMs>(
    mountTimestamp - initialMs
  );
  const lastActivityRef = useRef(mountTimestamp);
  const sessionStartRef = useRef(mountTimestamp);
  const accumulatedMillisecondsRef = useRef(initialMs);

  const calculateTotalMilliseconds = useCallback(
    (includeCurrentSession: boolean) => {
      const currentSessionMs = includeCurrentSession
        ? Date.now() - sessionStartRef.current
        : 0;
      return accumulatedMillisecondsRef.current + currentSessionMs;
    },
    []
  );

  const persistToStorage = useCallback((milliseconds: number) => {
    if (storageKeyRef.current) {
      localStorage.setItem(
        storageKeyRef.current,
        String(Math.round(milliseconds))
      );
    }
  }, []);

  const flushSessionToStorage = useCallback(() => {
    const sessionMs = Date.now() - sessionStartRef.current;
    accumulatedMillisecondsRef.current += sessionMs;
    sessionStartRef.current = Date.now();
    persistToStorage(accumulatedMillisecondsRef.current);
  }, [persistToStorage]);

  const saveCurrentSession = useCallback(() => {
    flushSessionToStorage();
    setElapsedSeconds(Math.floor(accumulatedMillisecondsRef.current / 1000));
  }, [flushSessionToStorage]);

  const isRunningRef = useRef(false);
  const flushRef = useRef(flushSessionToStorage);

  useEffect(() => {
    isRunningRef.current = isActive && !manuallyPaused;
    flushRef.current = flushSessionToStorage;
  });

  useEffect(
    () => () => {
      if (isRunningRef.current) {
        flushRef.current();
      }
    },
    []
  );

  const pause = useCallback(() => {
    if (isActive) {
      saveCurrentSession();
    }
    setManuallyPaused(true);
    setIsActive(false);
  }, [isActive, saveCurrentSession]);

  const resume = useCallback(() => {
    setManuallyPaused(false);
    setIsActive(true);
    sessionStartRef.current = Date.now();
    lastActivityRef.current = Date.now();
  }, []);

  const toggle = useCallback(() => {
    if (isActive) {
      pause();
    } else {
      resume();
    }
  }, [isActive, pause, resume]);

  useEffect(() => {
    if (manuallyPaused) {
      return;
    }

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (!isActive && !manuallyPaused) {
        setIsActive(true);
        sessionStartRef.current = Date.now();
      }
    };

    const handleVisibilityChange = () => {
      if (manuallyPaused) {
        return;
      }

      if (document.hidden && isActive) {
        saveCurrentSession();
        setIsActive(false);
      } else if (!document.hidden && !isActive) {
        handleActivity();
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("mousedown", handleActivity);
    window.addEventListener("touchstart", handleActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const inactivityInterval = setInterval(() => {
      if (manuallyPaused || !isActive) {
        return;
      }

      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity > inactivityThresholdMs) {
        saveCurrentSession();
        setIsActive(false);
      }
    }, 1000);

    const timerInterval = setInterval(() => {
      if (isActive && !manuallyPaused) {
        const totalMs = calculateTotalMilliseconds(true);
        setElapsedSeconds(Math.floor(totalMs / 1000));
      }
    }, 1000);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("mousedown", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(inactivityInterval);
      clearInterval(timerInterval);
    };
  }, [
    isActive,
    manuallyPaused,
    inactivityThresholdMs,
    saveCurrentSession,
    calculateTotalMilliseconds,
  ]);

  const getElapsedSeconds = useCallback(() => {
    const totalMs = calculateTotalMilliseconds(isActive && !manuallyPaused);
    return Math.floor(totalMs / 1000);
  }, [isActive, manuallyPaused, calculateTotalMilliseconds]);

  const reset = useCallback(() => {
    setElapsedSeconds(0);
    accumulatedMillisecondsRef.current = 0;
    const now = Date.now();
    sessionStartRef.current = now;
    lastActivityRef.current = now;
    setSessionStartedAt(now);
    if (storageKeyRef.current) {
      localStorage.removeItem(storageKeyRef.current);
    }
  }, []);

  return {
    elapsedSeconds,
    isActive,
    getElapsedSeconds,
    reset,
    pause,
    resume,
    toggle,
    sessionStartedAt,
  };
};
